import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Handshake, Home, Users, Bell, Plus, Check, Trash2, Pencil, ExternalLink } from "lucide-react";

const TYPES = [
  { value: "closing", label: "Closing", icon: Home, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "meeting", label: "Meeting", icon: Handshake, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "investor", label: "Investor meeting", icon: Users, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "custom", label: "Event", icon: CalendarClock, cls: "bg-slate-100 text-slate-700 border-slate-200" },
];
const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.value, t]));
const REMINDER_OPTS = [
  { m: 10080, label: "1 week before" },
  { m: 1440, label: "1 day before" },
  { m: 60, label: "1 hour before" },
  { m: 0, label: "At start time" },
];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const countdown = (iso) => {
  const diff = new Date(iso) - new Date();
  if (diff < 0) return "past";
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000), m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
};

const emptyForm = () => ({ title: "", event_type: "closing", start_at: "", contact: "", notes: "", lead_id: "none", reminders: [1440, 60], email_enabled: true });

export default function Schedule() {
  const [events, setEvents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showDone, setShowDone] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data.events || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load schedule"); }
  };
  useEffect(() => {
    load();
    api.get("/leads").then(({ data }) => setLeads(Array.isArray(data) ? data : (data.leads || data.items || []))).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ title: e.title, event_type: e.event_type, start_at: toLocalInput(e.start_at), contact: e.contact || "",
      notes: e.notes || "", lead_id: e.lead_id || "none", reminders: (e.reminders || []).map((r) => r.minutes_before), email_enabled: !!e.email_enabled });
    setOpen(true);
  };
  const toggleReminder = (m) => setForm((p) => ({ ...p, reminders: p.reminders.includes(m) ? p.reminders.filter((x) => x !== m) : [...p.reminders, m] }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    if (!form.start_at) return toast.error("Pick a date & time");
    setBusy(true);
    const payload = {
      title: form.title, event_type: form.event_type, start_at: new Date(form.start_at).toISOString(),
      contact: form.contact, notes: form.notes, lead_id: form.lead_id === "none" ? null : form.lead_id,
      reminders: form.reminders, email_enabled: form.email_enabled,
    };
    try {
      if (editing) { await api.put(`/events/${editing.id}`, payload); toast.success("Event updated"); }
      else { await api.post("/events", payload); toast.success("Event scheduled"); }
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save event"); }
    finally { setBusy(false); }
  };
  const complete = async (id) => { try { await api.post(`/events/${id}/complete`); toast.success("Marked done"); await load(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { try { await api.delete(`/events/${id}`); toast.success("Deleted"); await load(); } catch { toast.error("Failed"); } };

  const visible = events.filter((e) => (showDone ? true : e.status !== "completed") && (filter === "all" || e.event_type === filter));
  const leadLabel = (id) => { const l = leads.find((x) => x.id === id); return l ? (l.property_address || l.address || l.owner_name || l.lead_id_display || id.slice(0, 8)) : "lead"; };

  return (
    <div data-testid="schedule-page">
      <PageHeader title="Schedule" subtitle="Closings, meetings & investor reminders" action={
        <Button onClick={openNew} data-testid="new-event-btn"><Plus className="w-4 h-4 mr-1.5" /> New Event</Button>
      } />
      <div className="p-4 sm:p-6 lg:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-2" data-testid="schedule-filters">
          {["all", ...TYPES.map((t) => t.value)].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"}`}>
              {f === "all" ? "All" : TYPE_MAP[f].label}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} data-testid="show-completed" /> Show completed
          </label>
        </div>

        {visible.length === 0 ? (
          <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-md" data-testid="schedule-empty">
            <CalendarClock className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No events scheduled. Click <b>New Event</b> to add a closing, meeting or investor call.
          </div>
        ) : (
          <div className="space-y-3" data-testid="events-list">
            {visible.map((e) => {
              const t = TYPE_MAP[e.event_type] || TYPE_MAP.custom; const Icon = t.icon;
              const past = new Date(e.start_at) < new Date();
              return (
                <div key={e.id} data-testid={`event-${e.id}`} className={`bg-card border border-border rounded-md p-4 flex items-start gap-4 ${e.status === "completed" ? "opacity-60" : ""}`}>
                  <div className={`w-10 h-10 rounded-md border flex items-center justify-center shrink-0 ${t.cls}`}><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold truncate">{e.title}</span>
                      <Badge variant="outline" className={`text-[10px] ${t.cls}`}>{t.label}</Badge>
                      {e.status === "completed" && <Badge variant="outline" className="text-[10px] bg-slate-100">Done</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      {fmtWhen(e.start_at)} · <span className={past ? "text-muted-foreground" : "text-accent font-medium"}>{countdown(e.start_at)}</span>
                    </div>
                    {(e.contact || e.notes) && <div className="text-xs text-muted-foreground mt-1 truncate">{e.contact}{e.contact && e.notes ? " — " : ""}{e.notes}</div>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {(e.reminders || []).map((r) => (
                        <span key={r.minutes_before} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${r.sent ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          <Bell className="w-2.5 h-2.5" />{r.minutes_before === 0 ? "at start" : r.minutes_before >= 1440 ? `${r.minutes_before / 1440}d` : r.minutes_before >= 60 ? `${r.minutes_before / 60}h` : `${r.minutes_before}m`}{r.sent ? " ✓" : ""}
                        </span>
                      ))}
                      {e.lead_id && <Link to={`/leads/${e.lead_id}`} className="text-[10px] text-accent inline-flex items-center gap-1 underline" data-testid={`event-lead-${e.id}`}><ExternalLink className="w-2.5 h-2.5" />{leadLabel(e.lead_id)}</Link>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {e.status !== "completed" && <Button size="icon" variant="ghost" onClick={() => complete(e.id)} data-testid={`event-complete-${e.id}`} title="Mark done"><Check className="w-4 h-4" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(e)} data-testid={`event-edit-${e.id}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(e.id)} data-testid={`event-delete-${e.id}`} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle>
            <DialogDescription>Set a date and reminders for a closing, meeting or investor call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Closing — 123 Main St" data-testid="event-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm((p) => ({ ...p, event_type: v }))}>
                  <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Date &amp; time</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm((p) => ({ ...p, start_at: e.target.value }))} data-testid="event-datetime" /></div>
            </div>
            <div className="space-y-1.5"><Label>Contact / investor (optional)</Label><Input value={form.contact} onChange={(e) => setForm((p) => ({ ...p, contact: e.target.value }))} placeholder="e.g. Acme Title / John (investor)" data-testid="event-contact" /></div>
            <div className="space-y-1.5"><Label>Link to lead (optional)</Label>
              <Select value={form.lead_id} onValueChange={(v) => setForm((p) => ({ ...p, lead_id: v }))}>
                <SelectTrigger data-testid="event-lead"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{leads.slice(0, 100).map((l) => <SelectItem key={l.id} value={l.id}>{l.property_address || l.address || l.owner_name || l.id.slice(0, 8)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="event-notes" /></div>
            <div className="space-y-1.5">
              <Label>Reminders</Label>
              <div className="flex flex-wrap gap-2" data-testid="event-reminders">
                {REMINDER_OPTS.map((r) => (
                  <button key={r.m} type="button" onClick={() => toggleReminder(r.m)} data-testid={`reminder-${r.m}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${form.reminders.includes(r.m) ? "bg-accent/15 text-accent border-accent/40" : "bg-card text-muted-foreground border-border"}`}>
                    <Bell className="w-3 h-3 inline mr-1" />{r.label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground mt-1 cursor-pointer">
                <input type="checkbox" checked={form.email_enabled} onChange={(e) => setForm((p) => ({ ...p, email_enabled: e.target.checked }))} data-testid="event-email" /> Also email me the reminder
              </label>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="event-save">{busy ? "Saving…" : editing ? "Save changes" : "Schedule event"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
